/**
 * localStorage-backed user preferences (per device).
 *
 * Pattern mirrors `lib/quiz/persistedStats.ts`:
 *   - Versioned schema so we can roll forward without breaking old reads.
 *   - Single try/catch around the localStorage call so storage failures
 *     (private browsing, quota, disabled) degrade silently to defaults.
 *   - Cross-mount + cross-tab reactivity via a custom event + the
 *     native "storage" event.
 *
 * Current preferences:
 *   - spotLayout: "table" | "simplified"
 *
 * The toggle UI in Settings flips this; SpotCard reads it via
 * `useSpotLayout()`. Default is "table" — the current poker-felt oval.
 */

import { useEffect, useState } from "react";

const STORAGE_KEY = "pt:prefs";
const CHANGE_EVENT = "pt:prefs-change";
const SCHEMA_VERSION = 1;

export type SpotLayout = "table" | "simplified";
const DEFAULT_SPOT_LAYOUT: SpotLayout = "table";

interface StoredPrefs {
  v: number;
  spotLayout: SpotLayout;
}

function isSpotLayout(value: unknown): value is SpotLayout {
  return value === "table" || value === "simplified";
}

/** Read the spot-layout preference. Returns the default on first run,
 *  malformed JSON, schema-version mismatch, or storage unavailable. */
export function loadSpotLayout(): SpotLayout {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SPOT_LAYOUT;
    const parsed = JSON.parse(raw) as Partial<StoredPrefs>;
    if (parsed.v !== SCHEMA_VERSION) return DEFAULT_SPOT_LAYOUT;
    if (!isSpotLayout(parsed.spotLayout)) return DEFAULT_SPOT_LAYOUT;
    return parsed.spotLayout;
  } catch {
    return DEFAULT_SPOT_LAYOUT;
  }
}

/** Write the spot-layout preference. Dispatches `pt:prefs-change` so
 *  any `useSpotLayout()` consumer in the same tab updates immediately.
 *  Silent no-op on storage failure. */
export function saveSpotLayout(value: SpotLayout): void {
  try {
    const data: StoredPrefs = { v: SCHEMA_VERSION, spotLayout: value };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // Storage unavailable — UI stays on the in-memory value the caller
    // already updated (if they used the hook). No persisted state, but
    // the current session continues to honor the choice.
  }
}

/** React hook: returns `[current, setCurrent]`. Subscribes to in-tab
 *  changes (CustomEvent from `saveSpotLayout`) and cross-tab changes
 *  (native `storage` event) so two open tabs stay consistent. */
export function useSpotLayout(): [SpotLayout, (value: SpotLayout) => void] {
  const [value, setValue] = useState<SpotLayout>(loadSpotLayout);

  useEffect(() => {
    const handler = () => setValue(loadSpotLayout());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const update = (next: SpotLayout) => {
    saveSpotLayout(next);
    setValue(next);
  };

  return [value, update];
}
